import { parseDate } from 'chrono-node'

export const MINUTES_PER_DAY = 24 * 60

/*
 * Parse various time input formats into HH:mm format.
 *
 * Supported formats:
 * - "310" or "0310" → "03:10"
 * - "3:10" or "3:10pm" → "15:10"
 * - "3pm" → "15:00"
 * - "15:30" → "15:30"
 * - Natural language via chrono-node
 */
export function parseTimeInput(input: string): string | null {
  const trimmed = input.trim().toLowerCase()
  if (!trimmed) {
    return null
  }

  // Try numeric patterns first (e.g., "310", "0310", "1530")
  const numericResult = parseNumericTime(trimmed)
  if (numericResult) {
    return numericResult
  }

  // Try chrono-node for natural language parsing
  const parsed = parseDate(input)
  if (parsed) {
    const hours = parsed.getHours()
    const minutes = parsed.getMinutes()
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
  }

  return null
}

/*
 * Parse numeric time formats like "310", "0310", "1530".
 *
 * Handles:
 * - 1-2 digits: hour only ("3" → 3:00, "15" → 15:00)
 * - 3 digits: h:mm ("310" → 3:10)
 * - 4 digits: hh:mm ("0310" → 03:10, "1530" → 15:30)
 * - AM/PM suffix: "3pm" → 15:00, "310am" → 03:10, "5a" → 05:00, "517p" → 17:17
 *
 * Ambiguous times (no AM/PM, hours 1-11):
 * - Hours 1-6 default to PM (typical appointment hours)
 * - Hours 7-11 default to AM (typical morning hours)
 * - 4-digit input treated as 24-hour format (no ambiguity)
 */
export function parseNumericTime(input: string): string | null {
  // Remove all non-alphanumeric characters except for am/pm
  const cleaned = input.replace(/[^0-9apm]/gi, '')

  // Check for am/pm suffix (accepts "a"/"p" or "am"/"pm")
  const isPM = /p(m)?$/i.test(cleaned)
  const isAM = /a(m)?$/i.test(cleaned)
  const digits = cleaned.replace(/[apm]/gi, '')

  if (!digits || digits.length > 4) {
    return null
  }

  let hours: number
  let minutes: number

  if (digits.length <= 2) {
    // "3" → 3:00, "15" → 15:00
    hours = parseInt(digits, 10)
    minutes = 0
  } else if (digits.length === 3) {
    // "310" → 3:10, "130" → 1:30
    hours = parseInt(digits[0], 10)
    minutes = parseInt(digits.slice(1), 10)
  } else {
    // "0310" → 03:10, "1530" → 15:30
    hours = parseInt(digits.slice(0, 2), 10)
    minutes = parseInt(digits.slice(2), 10)
  }

  // Validate ranges
  if (minutes >= 60) {
    return null
  }

  // Validate 12-hour format when AM/PM is explicitly specified
  // Hours must be 1-12 (0 and 13+ are invalid in 12-hour format)
  if ((isPM || isAM) && (hours === 0 || hours > 12)) {
    return null
  }

  // Handle 12-hour format with am/pm
  if (isPM && hours < 12) {
    hours += 12
  } else if (isAM && hours === 12) {
    hours = 0
  }

  // Ambiguous times (1-6) default to PM for typical appointment hours
  if (!isPM && !isAM && hours >= 1 && hours <= 6 && digits.length < 4) {
    hours += 12
  }

  if (hours >= 24) {
    return null
  }

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
}

export function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

/*
 * Returns startTime + 1 minute, or null if that would exceed maxTime or wrap past midnight.
 */
export function calculateMinEndTime(
  startTime: string,
  minTime: string,
  maxTime: string
): string | null {
  if (!startTime) {
    return minTime
  }

  const startMinutes = timeToMinutes(startTime)
  const maxMinutes = timeToMinutes(maxTime)
  const calculatedMinutes = startMinutes + 1
  if (calculatedMinutes >= MINUTES_PER_DAY || calculatedMinutes > maxMinutes) {
    return null
  }

  const hours = Math.floor(calculatedMinutes / 60)
  const mins = calculatedMinutes % 60
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`
}
